// react
import React, { useEffect, useRef, useState } from "react";

// components
import { Spinner } from "@performyard/py-components";

// styles
import css from "./Radio.module.scss";

type RadioButtonProps = {
	/** renders a spinner if, for example, are waiting on a request */
	isLoading?: boolean;
	/** Any additional classes */
	className?: string;
	/** input onChange hook */
	onChange: (value: boolean) => void;
	/** current state (true|false) */
	value: boolean;
};

/**
 * @deprecated Use the RadioButtons component from the py-components package instead
 * Creates a radio input button.
 * The input will handle animation between a selected and a not-selected state.
 * This animation relies on a changing value and does not happen in the
 * onChange handler.
 *
 * ```jsx
 *  <RadioButton value={true} onChange={handleChange} />
 * ```
 */
const RadioButton = ({ className = "", isLoading = false, onChange, value }: RadioButtonProps) => {
	// sends the onchange hook to the owner
	const handleChange = (): void => {
		onChange(!value);
	};

	// used to manage animating from true -> false (or vice versa)
	const [changingTo, setChangingTo] = useState<string | null>(null);

	// create an instance variable for our timeout
	const changeTimeout = useRef<NodeJS.Timeout>();

	// on unmount, clear timeout so we don't update the state on an unmounted
	// component.
	useEffect(
		() => () => {
			clearTimeout(changeTimeout.current);
		},
		[]
	);

	// listens for changes to the value so it can handle transition animations
	// to the new value prop
	useEffect(() => {
		setChangingTo(value ? "to-true" : "to-false");

		changeTimeout.current = setTimeout(() => {
			setChangingTo(null);
		}, 200);
	}, [value]);

	// if loading, show a spinner.
	if (isLoading) {
		return <Spinner size={20} data-testid="spinner" />;
	}

	return (
		<div
			data-testid={value ? "true" : "false"}
			onClick={handleChange}
			className={`${css.main} ${className} ${changingTo ? css[changingTo] : ""} ${
				value ? css.isTrue : css.isFalse
			}`}
		>
			<div className={css.fill} />
		</div>
	);
};

/** @deprecated Use the RadioButtons component from the py-components package instead */
export default RadioButton;
